import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useRef, useLayoutEffect, useState, useCallback } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { HashLink } from "react-router-hash-link";
import { useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { label: "HOME", to: "/", exact: true },
  { label: "EXPLORE", to: "/services", exact: true },
  { label: "ARTIST ALLEY", to: "/explore/artist-alley", exact: true },
  { label: "EVENTS", to: "/explore/events", exact: true },
  { label: "ABOUT", to: "/about", exact: true },
];

const Navbar = () => {
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = useState(null);

  const navRef = useRef(null);
  const [underline, setUnderline] = useState({ left: 0, width: 0 });

  const isActive = useCallback(
    (path, exact) =>
      exact ? location.pathname === path : location.pathname.startsWith(path),
    [location.pathname]
  );

  /* ---------- SLIDING UNDERLINE ---------- */
  useLayoutEffect(() => {
    if (!navRef.current) return;

    const activeIndex = NAV_ITEMS.findIndex((item) =>
      isActive(item.to, item.exact)
    );

    if (activeIndex === -1) return;

    const button = navRef.current.children[activeIndex];
    if (!button) return;

    setUnderline({
      left: button.offsetLeft,
      width: button.offsetWidth,
    });
  }, [isActive]);

  return (
    <Box sx={{ mt: 8 }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "rgba(15,124,164,0.95)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* ---------- LOGO ---------- */}
            <HashLink to="/" style={{ textDecoration: "none" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  animation: "logoFloat 5s ease-in-out infinite",
                  "&:hover": {
                    animationPlayState: "paused",
                  },
                  "@keyframes logoFloat": {
                    "0%": { transform: "translateY(0px)" },
                    "50%": { transform: "translateY(-3px)" },
                    "100%": { transform: "translateY(0px)" },
                  },
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Den Den Claw Logo"
                  sx={{
                    height: 40,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      filter:
                        "drop-shadow(0 0 8px rgba(153,204,237,0.8))",
                    },
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    display: { xs: "none", md: "block" },
                  }}
                >
                  Den Den Claw
                </Typography>
              </Box>
            </HashLink>

            {/* ---------- MOBILE MENU ---------- */}
            <Box sx={{ ml: "auto", display: { xs: "flex", md: "none" } }}>
              <IconButton onClick={(e) => setAnchorElNav(e.currentTarget)}>
                <MenuIcon sx={{ color: "white" }} />
              </IconButton>

              <Menu
                anchorEl={anchorElNav}
                open={Boolean(anchorElNav)}
                onClose={() => setAnchorElNav(null)}
              >
                {NAV_ITEMS.map((item) => (
                  <MenuItem
                    key={item.label}
                    component={HashLink}
                    to={item.to}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                <MenuItem component={HashLink} to="/contact">
                  ✨ VISIT ✨
                </MenuItem>
              </Menu>
            </Box>

            {/* ---------- DESKTOP NAV ---------- */}
            <Box
              ref={navRef}
              sx={{
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              {NAV_ITEMS.map((item) => (
                <HashLink
                  key={item.label}
                  to={item.to}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    sx={{
                      color: "white",
                      px: 3,
                      py: 2,
                      fontWeight: 600,
                      opacity: isActive(item.to, item.exact) ? 1 : 0.85,
                      textTransform: "none",
                    }}
                  >
                    {item.label}
                  </Button>
                </HashLink>
              ))}

              {/* ---------- SLIDING UNDERLINE ---------- */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 6,
                  height: 3,
                  borderRadius: 999,
                  backgroundColor: "#99cced",
                  transition: "all 0.35s cubic-bezier(.4,0,.2,1)",
                  left: underline.left,
                  width: underline.width,
                }}
              />
            </Box>

            {/* ---------- VISIT CTA ---------- */}
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <HashLink to="/contact">
                <Button
                  sx={{
                    ml: 2,
                    px: 4,
                    borderRadius: 999,
                    fontWeight: 700,
                    color: "#0f7ca4",
                    backgroundColor: "#99cced",
                    textTransform: "none",
                    position: "relative",
                    overflow: "hidden",
                    letterSpacing: "0.04em",
                    animation: "visitBounce 0.8s ease-out 0.4s 1",
                    boxShadow: "0 4px 10px rgba(15,124,164,0.25)",
                    transition: "all 0.25s ease",

                    /* ---------- SHINE SWEEP ---------- */
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-120%",
                      width: "60%",
                      height: "100%",
                      background:
                        "linear-gradient(120deg, transparent, rgba(255,255,255,0.45), transparent)",
                      transform: "skewX(-20deg)",
                    },

                    "&:hover::after": {
                      left: "140%",
                      transition: "left 0.7s ease",
                    },

                    "&:hover": {
                      color: "#ffffff",
                      backgroundColor: "#99cced",
                      boxShadow:
                        "0 0 0 2px rgba(15,124,164,0.6), 0 8px 18px rgba(15,124,164,0.35)",
                      transform: "translateY(-2px) scale(1.04)",
                    },

                    /* ---------- BOUNCE KEYFRAMES ---------- */
                    "@keyframes visitBounce": {
                      "0%": { transform: "translateY(0)" },
                      "30%": { transform: "translateY(-6px)" },
                      "55%": { transform: "translateY(0)" },
                      "75%": { transform: "translateY(-3px)" },
                      "100%": { transform: "translateY(0)" },
                    },
                  }}
                >
                  ✨ VISIT ✨
                </Button>
              </HashLink>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </Box>
  );
};

export default Navbar;
